import React, {useEffect, useState} from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import {
  Button, Table,
  Typography, notification, 
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../../configure/components/ConfigHeading';
import { selectEmailRecipientsRequest } from '../../../../state/ui/configuration/communication/emailRecipientsUiReducer';
import { selectEmailRecipients } from '../../../../state/domain/communication/emailRecipientReducer';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { editEmailRecipient as editEmailRecipientAction, requestEmailRecipients, requestDeleteEmailRecipient } from '../../../../state/ui/configuration/communication/emailRecipientsUiActions';
import EditEmailRecipient from './EditEmailRecipient';
import {
  roleNames,
  userIsAdmin,
  userIsConsoleAdmin,
  userIsCompanyAdmin, 
} from '../../configure/company/user-util';
import ConfirmButton from '../../../../components/buttons/ConfirmButton';
import {userIsAdminConsole} from "../../../../utility/security/security-util";

const { Column } = Table;
const { Text } = Typography;

// TABLE
function EmailRecipientsTable({
  isLoading, recipients, onEditButton, sessionUser, onDeleteButton,
}) {
  const RowTotal = () => (
    <div>
      <Text>{`${recipients.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  // EXTRA FILTERS

  // SORTS
  function sortEmailRecipientsByEmail(a, b) {
    const aStr = `${a.emailAddress}`;
    const bStr = `${b.emailAddress}`;
    return aStr.localeCompare(bStr);
  }
  // TABLE SETTINGS, EXPANDED ROW, COLUMNS
  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={recipients}
      rowKey={recipient => recipient.emailAddress}
    >
      <Column
        sorter={sortEmailRecipientsByEmail}
        title={<FormattedMessage id="email" />}
        dataIndex="emailAddress"
        key="emailAddress"
      />
      { userIsAdmin(sessionUser) || userIsConsoleAdmin(sessionUser) || userIsCompanyAdmin(sessionUser)
        ? (
          <Column
            className="via-action-column"
            align="center"
            width="70px"
            render={(text, record) => (
              <div className="button-flex-col">
                  <ConfirmButton size="small" type="danger" onClick={() => onDeleteButton(record.recipientId, record.emailAddress)} icon="stop">
                    <FormattedMessage id="delete" />
                  </ConfirmButton>
              </div>
            )}
          />
        ) : ''}
    </Table>
  );
}

EmailRecipientsTable.propTypes = {
  isLoading: PropTypes.bool,
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      emailAddress: PropTypes.string.isRequired,
    }),
  ),
  onEditButton: PropTypes.func.isRequired,
  sessionUser: PropTypes.shape({
    emailAddress: PropTypes.string.isRequired,
  }).isRequired,
  onDeleteButton: PropTypes.func.isRequired,
};

EmailRecipientsTable.defaultProps = {
  isLoading: false,
  recipients: [],
};

function ManageEmailRecipients({
  isLoading, loadPageData, recipients, deleteEmailRecipient, editRecipient, sessionUser, 
}) {
  // Manage company - hard-coded for now
  const [manageCompany] = useState('PURCO');

  // Load email recipients on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData(manageCompany);
  }, [loadPageData, manageCompany]);
  const intl = useIntl();

  // Extra validation helper
  const doesEmailExist = (recipientId, value) => {
    const matchedRecipient = recipients.find(row => row.emailAddress.toString().toLowerCase() === (value || '').toString().toLowerCase());
    return matchedRecipient ? matchedRecipient.recipientId !== recipientId : false;
  };

  const onCreateOrEdit = (recipient) => {
    if (!sessionUser.linkedRoles.find(linkedRole => linkedRole === roleNames.ADMIN || linkedRole === roleNames.CONSOLE_ADMIN || linkedRole === roleNames.COMPANY_ADMIN )) {
      notification.warn({
        message: intl.formatMessage({ id: 'insufficientPermissions' }),
        description: intl.formatMessage({ id: 'notAdministrator' }),
        duration: null,
      });
    } else {
      editRecipient(recipient);
    }
  };

  const CreateButton = () => (
    <Button type="primary" onClick={() => onCreateOrEdit(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  return (
    <ConfigHeading
      titleId={userIsAdminConsole(sessionUser) ? 'emailRecipients' : 'emailRecipients'}
      descriptionId="emailRecipientsFunction"
      actions={userIsAdmin(sessionUser) || userIsConsoleAdmin(sessionUser) || userIsCompanyAdmin(sessionUser) ? <CreateButton /> : ''}
    >
      <EmailRecipientsTable
        isLoading={isLoading}
        recipients={recipients}
        onEditButton={onCreateOrEdit}
        sessionUser={sessionUser}
        onDeleteButton={deleteEmailRecipient}
      />
      <EditEmailRecipient manageCompany={manageCompany} emailAddressValidator={doesEmailExist}/>
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
ManageEmailRecipients.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      emailAddress: PropTypes.string.isRequired,
    }),
  ),
  deleteEmailRecipient: PropTypes.func.isRequired,
  editRecipient: PropTypes.func.isRequired,
  sessionUser: PropTypes.shape({
    emailAddress: PropTypes.string.isRequired,
  }).isRequired,
  
};

ManageEmailRecipients.defaultProps = {
  isLoading: false,
  recipients: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectEmailRecipientsRequest(state);
  const allEmailRecipients = selectEmailRecipients(state);
  
  return {
    sessionUser: selectSessionUser(state),
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    recipients: allEmailRecipients,
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: (manageCompany) => dispatch(requestEmailRecipients(manageCompany)),
  // eslint-disable-next-line max-len
  editRecipient: emailRecipient => dispatch(editEmailRecipientAction(emailRecipient ? emailRecipient.recipientId : null)),
  deleteEmailRecipient: (recipientId, email) => dispatch(requestDeleteEmailRecipient(recipientId, email)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ManageEmailRecipients));
